import { useCallback, useState } from "react";

/**
 * useQueryParams
 *
 * Reads, validates, and updates URL query params.
 * URL is kept in sync via history.replaceState and React state is updated
 * in the same call so the component re-renders immediately.
 *
 * @param {Object} schema  Map of param key → { defaultValue, permittedValues? }
 *   - defaultValue     Used when the param is missing or fails validation.
 *   - permittedValues  Optional array of accepted string values. When omitted
 *                      every non-null string is accepted.
 *
 * @example
 * const schema = {
 *   geo_entity:    { defaultValue: null, permittedValues: ["circle", "zone", "area"] },
 *   kpi_parameter: { defaultValue: null },
 * };
 *
 * @returns {{ params, setParam, setParams, clearParam }}
 */
const parseParams = (schema) => {
  const sp = new URLSearchParams(window.location.search);
  const result = {};
  let urlNeedsSanitizing = false;

  Object.keys(schema).forEach((key) => {
    const { defaultValue, permittedValues } = schema[key];
    const raw = sp.get(key);
    const isValid =
      raw !== null && (!permittedValues || permittedValues.includes(raw));

    if (!isValid && raw !== null) {
      sp.delete(key);
      urlNeedsSanitizing = true;
    }

    result[key] = isValid
      ? raw
      : defaultValue !== undefined
        ? defaultValue
        : null;
  });

  if (urlNeedsSanitizing) {
    persistToUrl(sp);
  }

  return result;
};

const persistToUrl = (sp) => {
  const search = sp.toString();
  const updatedUrl = `${window.location.pathname}${search.length > 0 ? "?" + search : ""}`;
  window.history.replaceState({}, "", updatedUrl);
};

const useQueryParams = (schema = {}) => {
  const [params, setParamsState] = useState(() => parseParams(schema));

  const setParam = useCallback((key, value) => {
    const sp = new URLSearchParams(window.location.search);
    if (value !== null && value !== undefined && value !== "") {
      sp.set(key, value);
    } else {
      sp.delete(key);
    }
    persistToUrl(sp);
    setParamsState((prev) => ({ ...prev, [key]: value ?? null }));
  }, []);

  const setParams = useCallback((updates) => {
    const sp = new URLSearchParams(window.location.search);
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        sp.set(key, value);
      } else {
        sp.delete(key);
      }
    });
    persistToUrl(sp);
    setParamsState((prev) => {
      const next = { ...prev };
      Object.entries(updates).forEach(([key, value]) => {
        next[key] = value ?? null;
      });
      return next;
    });
  }, []);

  const clearParam = useCallback(
    (key) => {
      const sp = new URLSearchParams(window.location.search);
      sp.delete(key);
      persistToUrl(sp);
      setParamsState((prev) => ({
        ...prev,
        [key]: schema[key]?.defaultValue ?? null,
      }));
    },
    [schema]
  );

  return { params, setParam, setParams, clearParam };
};

export default useQueryParams;
